import { useCallback, useRef, useSyncExternalStore } from 'react'
import { filter, type Observable, skip } from 'rxjs'
import type { IBehaviorObservable, IWrappedBehaviorObservable } from './IBehaviorObservable'

export function useObservable<T>(observable: Observable<T>, snapshot: () => T): T {
  return useSyncExternalStore((callback) => {
    const sub = observable.subscribe(callback)
    return () => sub.unsubscribe()
  }, snapshot)
}

export function useBehaviorObservable<T>(observable: IBehaviorObservable<T>): T
export function useBehaviorObservable<T>(observable: IBehaviorObservable<T> | undefined): T | undefined
export function useBehaviorObservable<T>(observable: IBehaviorObservable<T> | undefined): T | undefined {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!observable) return () => {}
      const sub = observable.subscribe(callback)
      return () => sub.unsubscribe()
    },
    [observable],
  )

  const getSnapshot = useCallback(() => observable?.value, [observable])

  return useSyncExternalStore(subscribe, getSnapshot)
}

export function useWrappedObservable<T>(wrappedObservable: IWrappedBehaviorObservable<T>): T {
  return useObservable(wrappedObservable.observable$, () => wrappedObservable.value)
}

export function useFilteredObservableByValue<T>(event$: Observable<T>, filterValue: T): number {
  const predicate = useCallback((value: T) => value === filterValue, [filterValue])

  return useFilteredObservableByPredicate(event$, predicate)
}

export function useFilteredObservableByPredicate<T>(
  event$: Observable<T>,
  predicate: (value: T) => boolean,
): number {
  const versionRef = useRef(0)

  const subscribe = useCallback(
    (callback: () => void) => {
      const sub = event$.pipe(filter(predicate)).subscribe(() => {
        versionRef.current++
        callback()
      })

      return () => sub.unsubscribe()
    },
    [event$, predicate],
  )

  return useSyncExternalStore(subscribe, () => versionRef.current)
}
